import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch } from "./../redux/hooks";
import { useAppSelector } from "../redux/hooks";
import { usePostEventMutation } from "../redux/services/vectormapsAPI";
import {
  setSessionLastUpdate,
  setSessionId,
} from "../redux/slices/applicationSlice";
import { AnalyticsEvents } from "./../utils/analyticsTypes";
import { usePostSessionMutation } from "../redux/services/vectormapsAPI";

export const usePostAnalyticsEvent = () => {
  const dispatch = useAppDispatch();
  const { project, sessionId, sessionLastUpdate, queryParamsConfig } =
    useAppSelector((state) => state.application);

  const [postEvent, { isError }] = usePostEventMutation();
  const [postSession] = usePostSessionMutation();

  const sessionLastUpdateRef = useRef(sessionLastUpdate);
  const sessionPromiseRef = useRef<Promise<string> | null>(null);

  const handlePostEvent = useCallback(
    async (data: AnalyticsEvents) => {
      if (queryParamsConfig.debug === "enable") return;

      const now = Date.now();
      const thirtyMins = 30 * 60 * 1000;
      const last = sessionLastUpdateRef.current
        ? new Date(sessionLastUpdateRef.current).getTime()
        : null;

      let currentSessionId = sessionId;

      // If there's no session ID or the last update was more than 30 minutes ago, create a new session
      if (!last || now - last > thirtyMins) {
        if (!sessionPromiseRef.current) {
          sessionPromiseRef.current = postSession({ project })
            .unwrap()
            .then(({ uuid }) => {
              dispatch(setSessionId(uuid));
              return uuid;
            })
            .finally(() => {
              sessionPromiseRef.current = null;
            });
        }

        try {
          currentSessionId = await sessionPromiseRef.current;
        } catch (e) {
          console.error(e);
        }
      }

      // Post the event with the current or new session ID
      postEvent({ project, uuid: currentSessionId, data });
      dispatch(setSessionLastUpdate(new Date().toISOString()));
    },
    [dispatch, postEvent, postSession, project, queryParamsConfig, sessionId],
  );

  useEffect(() => {
    sessionLastUpdateRef.current = sessionLastUpdate;
  }, [sessionLastUpdate]);

  useEffect(() => {
    if (isError) {
      console.error("Error posting analytics event");
    }
  }, [isError]);

  return { logAnalyticsEvent: handlePostEvent };
};
